import { PageHeader } from "@justworkshr/milo-components";
import { useState } from "react";
import {
  Outlet,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import styles from "./PreQualification.module.css";
import {
  PRE_QUALIFY_TITLE,
  BACK_TO_APP_HOME,
  BACK_TO_BENEFITS,
  BACK_TO_DASHBOARD,
  HI_URL,
  UPSELL_URL,
} from "pages/PreQualification/utils/constants";
import Error from "uicomponents/Error";
import Alert from "uicomponents/Alert";
import { trackFormError } from "utils/analytics/trackEvent";
import { getCompanyId } from "utils/idHelpers";
import { HealthAppStepType } from "types";
import {
  getAppType,
  getCWRedirectUrl,
  getStepNumberFromURL,
} from "utils/helper";

type ContextType = {
  nextStep: () => void;
  previousStep: () => void;
  stepperNextStep: (stepNumber: number) => void;
  isEligibleForCoverage?: (value: boolean) => void;
};

interface IPreQualProps {
  data: HealthAppStepType;
}

export const usePreQualContext = () => {
  return useOutletContext<ContextType>();
};

const PreQualification = ({ ...props }: IPreQualProps): JSX.Element => {
  const isHealthInsuranceUpsellApp = getAppType() === "HEALTH_INSURANCE_UPSELL";
  const { data } = props;
  const healthAppUrl = isHealthInsuranceUpsellApp ? UPSELL_URL : HI_URL;

  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  const companyUuid = getCompanyId();

  const [stepError, setStepError] = useState(false);
  const [isNotEligibleForCoverage, setIsNotEligibleForCoverage] = useState(
    data?.healthInsuranceInformation?.isCoverageEligible === false
  );

  if (!data?.applications?.[0]) {
    return <Error />;
  }

  const nextStep = async () => {
    const currentStep = getStepNumberFromURL();
    try {
      const lastStep = currentStep >= 3;
      if (lastStep) {
        navigate(`${healthAppUrl}/${companyUuid}`);
        return;
      }
      navigate({
        pathname: `${currentStep + 1}`,
        search: `?${searchParams.toString()}`,
      });
    } catch (error: any) {
      setStepError(true);
      trackFormError(
        error,
        `error updating PreQual form step- ${error?.message}`
      );
      return;
    }
  };

  const previousStep = (): void => {
    const currentStep = getStepNumberFromURL();
    const isLastStep = getStepNumberFromURL() === 1;
    if (isLastStep) {
      const companyUuid = getCompanyId();
      navigate(`${healthAppUrl}/${companyUuid}`);
      return;
    }
    navigate({
      pathname: `${currentStep - 1}`,
      search: `?${searchParams.toString()}`,
    });
  };

  const stepperNextStep = (stepNumber: number) => {
    navigate({
      pathname: `${stepNumber}`,
      search: `?${searchParams.toString()}`,
    });
  };

  const isEligibleForCoverage = (value: boolean) => {
    setIsNotEligibleForCoverage(value);
  };

  const getBackButtonLink = (): JSX.Element => {
    if (getStepNumberFromURL() === 3 && isNotEligibleForCoverage) {
      const linkText = isHealthInsuranceUpsellApp
        ? BACK_TO_BENEFITS
        : BACK_TO_DASHBOARD;
      const linkhref = isHealthInsuranceUpsellApp
        ? getCWRedirectUrl("/benefits/offer")
        : getCWRedirectUrl("/onboarding_checklist");
      return <a href={linkhref}>{linkText}</a>;
    } else {
      return <a href={`${healthAppUrl}/${companyUuid}`}>{BACK_TO_APP_HOME}</a>;
    }
  };

  return (
    <div className={styles.prequalContainer}>
      <div className={styles.PreQualTitleContainer}>
        <PageHeader
          className={styles.prequalpageHeader}
          title={PRE_QUALIFY_TITLE}
          linkPrevious={getBackButtonLink()}
        />
      </div>
      <>
        <Outlet
          context={
            {
              previousStep,
              nextStep,
              stepperNextStep,
              isEligibleForCoverage,
            } as ContextType
          }
        />
        {stepError && <Alert />}
      </>
    </div>
  );
};
export default PreQualification;
